<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, shallowRef, watch } from 'vue';
import type { z } from 'zod';
import {
  eventsSchema,
  historySchema,
  modelsSchema,
  projectsSchema,
  statusSchema,
  summarySchema,
  taskSchema,
  tasksSchema,
  type NyxAnswer,
  type NyxClient,
  type NyxEvent,
  type NyxMode,
  type NyxOperation,
  type NyxRequest,
  type NyxStatus,
  type NyxTask,
} from '@atum-remote/nyx-link-protocol';
import type { RemoteDevice, RemoteUiAdapter } from '../model.js';
import type { NyxConnection } from '../nyx-session.js';
import { nyxInitialMessage, nyxRecovery, sendNyxRequest } from '../nyx-recovery.js';
import { createNyxActions, nyxMessageError, startNyxTask } from '../lib/nyx-actions.js';
import { navigateBrowserTab } from '../lib/browser-tab.js';
import type { Tone } from '../lib/format.js';
import {
  buildTranscript,
  clientLabel,
  composerHint,
  groupTasks,
  modeLabel,
  stateLabel,
  stateTone,
  taskEnded,
} from '../lib/nyx-presentation.js';
import AppIcon from './AppIcon.vue';
import BrandMark from './BrandMark.vue';
import EmptyState from './EmptyState.vue';
import NyxComposer from './NyxComposer.vue';
import NyxHistoryList from './NyxHistoryList.vue';
import NyxNewTask from './NyxNewTask.vue';
import NyxSessionDetail from './NyxSessionDetail.vue';
import NyxTaskList from './NyxTaskList.vue';
import NyxTranscript from './NyxTranscript.vue';
import StatusPill from './StatusPill.vue';

/**
 * The AI work page: a full-window remote viewer for Nyx Link's hosted coding
 * tasks, in the shape of a chat client. A collapsible sidebar lists tasks by
 * repository and captured history; the main column is the conversation with
 * the selected task, or the form that starts a new one.
 *
 * The web console opens it as its own page like the terminal; the installed
 * app shows it in place of the device list and returns through `back`.
 */
type Session = z.infer<typeof historySchema>['sessions'][number];

const props = defineProps<{
  device: RemoteDevice;
  adapter: RemoteUiAdapter | undefined;
  /** When set, the brand link navigates here; otherwise it emits `back`. */
  backHref?: string;
}>();
const emit = defineEmits<{ back: [] }>();
const connection = shallowRef<NyxConnection | null>(null);
const status = ref<NyxStatus | null>(null);
const busy = ref(false);
const connected = ref(false);
const error = ref('');
const projects = ref<z.infer<typeof projectsSchema>>({
  projects: [],
  cloudAvailable: false,
  truncated: false,
});
const tasks = ref<NyxTask[]>([]);
const taskTruncated = ref(false);
const project = ref('');
const client = ref<NyxClient>('codex');
const mode = ref<NyxMode>('ask_me');
const models = ref<z.infer<typeof modelsSchema>['models']>([]);
const model = ref('');
const effort = ref('');
const draft = ref('');
const prompt = ref('');
const selected = ref<NyxTask | null>(null);
const records = ref<{ sequence: number; event: NyxEvent }[]>([]);
const pending = ref<string[]>([]);
const cursor = ref(0);
const truncated = ref(false);
const history = ref<z.infer<typeof historySchema>>({ sessions: [], nextCursor: null, paged: true });
const historyLoaded = ref(false);
const session = ref<Session | null>(null);
const summary = ref<z.infer<typeof summarySchema> | null>(null);
const view = ref<'task' | 'new' | 'session'>('new');
const sidebar = ref<'tasks' | 'history'>('tasks');
const narrow = window.matchMedia?.('(max-width: 719px)');
const sidebarOpen = ref(!(narrow?.matches ?? false));
const transcript = ref<InstanceType<typeof NyxTranscript>>();
const uncertain = nyxRecovery(props.adapter, props.device.id);
const initialMessage = nyxInitialMessage(uncertain);
let mounted = true;
let timer: ReturnType<typeof setTimeout> | undefined;
let pollDelay = 2000;

const canControl = computed(() => props.device.nyx?.controlAvailable === true);
const clients = computed(() => status.value?.clients.filter((c) => c.installed) ?? []);
const modes = computed(() => clients.value.find((c) => c.client === client.value)?.modes ?? []);
const selectedModes = computed(
  () => status.value?.clients.find((c) => c.client === selected.value?.client)?.modes ?? [],
);
const efforts = computed(() => models.value.find((m) => m.id === model.value)?.efforts ?? []);
const ended = computed(() => taskEnded(selected.value));
const running = computed(() => selected.value?.state === 'running');
const actionable = computed(
  () => canControl.value && connected.value && !busy.value && !uncertain.value,
);
const canStart = computed(
  () =>
    actionable.value &&
    !nyxMessageError(draft.value, true) &&
    clients.value.some((entry) => entry.client === client.value) &&
    modes.value.includes(mode.value) &&
    projects.value.projects.some((p) => p.projectKey === project.value && p.mapped),
);
const projectNames = computed(() =>
  Object.fromEntries(projects.value.projects.map((entry) => [entry.projectKey, entry.name])),
);
const attention = computed(() => groupTasks(tasks.value).attention.length);
const transcriptItems = computed(() =>
  selected.value
    ? buildTranscript(records.value, { client: selected.value.client, pending: pending.value })
    : [],
);
const pendingCount = computed(() => pending.value.length);
const selectedProjectName = computed(() =>
  selected.value
    ? (projectNames.value[selected.value.projectKey] ?? selected.value.projectKey)
    : '',
);
const linkTone = computed<Tone>(() =>
  !props.device.nyx?.available
    ? 'muted'
    : connected.value
      ? 'ok'
      : busy.value && !status.value
        ? 'warn'
        : status.value
          ? 'danger'
          : 'muted',
);
const linkLabel = computed(() =>
  !props.device.nyx?.available
    ? 'Unavailable'
    : connected.value
      ? 'Connected'
      : busy.value && !status.value
        ? 'Connecting…'
        : status.value
          ? 'Disconnected'
          : 'Not connected',
);

async function run(operation: NyxOperation): Promise<unknown> {
  const request: NyxRequest = {
    version: 1,
    id: crypto.randomUUID(),
    issuedAt: Date.now(),
    operation,
  };
  return exchange(request);
}
async function exchange(request: NyxRequest): Promise<unknown> {
  const channel = connection.value;
  if (!channel?.isOpen()) throw new Error('Reconnect to Nyx Link first.');
  let response;
  try {
    response = await sendNyxRequest(channel, request, uncertain, (result) => {
      if (!mounted) return false;
      const operation = request.operation;
      if (operation.method === 'start') {
        showTask(taskSchema.parse(result));
        if (initialMessage.value?.startId === request.id && initialMessage.value.text.trim())
          return false; // Keep recovery available until the first send has its own receipt.
      } else if (operation.method === 'send') {
        if (selected.value?.id === operation.taskId && prompt.value === operation.text)
          prompt.value = '';
        const initial = initialMessage.value;
        if (initial?.taskId === operation.taskId && initial.text === operation.text) {
          if (draft.value === initial.text) draft.value = '';
          initialMessage.value = null;
        }
      }
      return true;
    });
  } catch (raised) {
    connected.value = channel.isOpen();
    throw raised;
  }
  if (response.error) {
    throw new Error(response.error);
  }
  return response.result;
}
/**
 * Runs one unit of work against the channel, which accepts a single request at
 * a time. A person's action sets `busy` so controls settle while it runs; a
 * background refresh only takes the channel lock, so polling never makes the
 * decision buttons blink.
 */
const action = createNyxActions({
  busy,
  error,
  active: () => mounted,
  settled(background) {
    connected.value = connection.value?.isOpen() ?? false;
    if (!background) {
      pollDelay = 2000;
      schedulePoll();
    }
  },
});
function closeNarrowSidebar() {
  if (narrow?.matches) sidebarOpen.value = false;
}
function showTask(task: NyxTask) {
  selected.value = task;
  records.value = [];
  cursor.value = 0;
  pending.value = [];
  truncated.value = false;
  view.value = 'task';
  closeNarrowSidebar();
}
async function loadTasks() {
  const result = tasksSchema.parse(await run({ method: 'tasks' }));
  tasks.value = result.tasks;
  taskTruncated.value = result.truncated;
  if (selected.value)
    selected.value = tasks.value.find((t) => t.id === selected.value?.id) ?? selected.value;
}
async function loadEvents() {
  if (!selected.value) return;
  const id = selected.value.id;
  for (let pageCount = 0; pageCount < 32; pageCount++) {
    const after = cursor.value;
    const page = eventsSchema.parse(await run({ method: 'events', taskId: id, after }));
    if (!mounted || selected.value?.id !== id) return;
    selected.value = page.task;
    pending.value = page.pending;
    cursor.value = page.nextCursor;
    const combined = [...records.value, ...page.events];
    records.value = combined.slice(-512);
    truncated.value ||= page.truncated || combined.length > 512;
    if (!page.hasMore) return;
    if (page.nextCursor <= after) throw new Error('Nyx history cursor did not advance.');
  }
}
async function loadModels() {
  models.value = [];
  model.value = '';
  effort.value = '';
  if (!clients.value.some((c) => c.client === client.value)) return;
  try {
    models.value = modelsSchema.parse(await run({ method: 'models', client: client.value })).models;
  } catch {
    error.value = 'Model discovery is unavailable. The client default is still available.';
  }
}
async function connect() {
  await action(async () => {
    await connection.value?.close();
    connection.value = null;
    status.value = null;
    if (!props.adapter?.openNyx || !props.device.nyx?.available)
      throw new Error(
        props.device.nyx?.explanation ?? 'Nyx access is unavailable for this device.',
      );
    const opened = await props.adapter.openNyx(props.device);
    if (!mounted) {
      await opened.close();
      return;
    }
    connection.value = opened;
    status.value = statusSchema.parse(await run({ method: 'status' }));
    projects.value = projectsSchema.parse(await run({ method: 'projects' }));
    project.value ||=
      projects.value.projects.find((p) => p.mapped)?.projectKey ??
      projects.value.projects[0]?.projectKey ??
      '';
    if (!clients.value.some((c) => c.client === client.value))
      client.value = clients.value[0]?.client ?? 'codex';
    if (!modes.value.includes(mode.value))
      mode.value = modes.value.includes('plan_only') ? 'plan_only' : (modes.value[0] ?? 'ask_me');
    await loadTasks();
    if (!selected.value && view.value !== 'session') {
      const groups = groupTasks(tasks.value);
      const first = groups.attention[0] ?? groups.active[0] ?? groups.finished[0];
      if (first) {
        showTask(first);
        sidebarOpen.value = !(narrow?.matches ?? false);
      }
    }
    await loadEvents();
    await loadModels();
  });
}
async function selectTask(task: NyxTask) {
  await action(async () => {
    showTask(task);
    await loadEvents();
  });
}
function newTask(projectKey?: string) {
  if (projectKey && projects.value.projects.some((p) => p.projectKey === projectKey))
    project.value = projectKey;
  view.value = 'new';
  closeNarrowSidebar();
}
async function start() {
  if (!canStart.value) return;
  await action(async () => {
    const request: NyxRequest = {
      version: 1,
      id: crypto.randomUUID(),
      issuedAt: Date.now(),
      operation: {
        method: 'start',
        projectKey: project.value,
        client: client.value,
        mode: mode.value,
        model: model.value || null,
        effort: effort.value || null,
      },
    };
    initialMessage.value = { startId: request.id, text: draft.value };
    await completeStart(request);
    await loadTasks();
    await loadEvents();
  });
}
async function completeStart(request: NyxRequest) {
  const initial = initialMessage.value;
  await startNyxTask({
    draft,
    initialText: initial?.startId === request.id ? initial.text : '',
    active: () => mounted && canControl.value,
    start: async () => taskSchema.parse(await exchange(request)),
    send: (taskId, text) => {
      // Once send begins its own idempotent receipt owns recovery. Never repeat
      // the continuation after an uncertain send; leave its text visible.
      initialMessage.value = { startId: request.id, taskId, text };
      prompt.value = text;
      return run({ method: 'send', taskId, text });
    },
  });
  if (mounted && initialMessage.value === initial) initialMessage.value = null;
}
async function send() {
  if (!selected.value || !prompt.value.trim() || !actionable.value) return;
  const invalid = nyxMessageError(prompt.value);
  if (invalid) {
    error.value = invalid;
    return;
  }
  await action(async () => {
    await run({ method: 'send', taskId: selected.value!.id, text: prompt.value });
    await loadEvents();
  });
}
async function decide(requestId: string, approve: boolean) {
  if (!selected.value || !actionable.value || !pending.value.includes(requestId)) return;
  await action(async () => {
    await run({ method: 'answer', taskId: selected.value!.id, requestId, approve });
    await loadEvents();
  });
}
async function answer(requestId: string, answers: NyxAnswer[]) {
  if (!selected.value || !actionable.value || !pending.value.includes(requestId)) return;
  await action(async () => {
    await run({ method: 'answer_question', taskId: selected.value!.id, requestId, answers });
    await loadEvents();
  });
}
async function stop(interrupt: boolean) {
  if (!selected.value || !actionable.value) return;
  await action(async () => {
    await run({ method: interrupt ? 'interrupt' : 'stop', taskId: selected.value!.id });
    await loadTasks();
    await loadEvents();
  });
}
async function setMode(event: Event) {
  const next = (event.target as HTMLSelectElement).value as NyxMode;
  if (!selected.value || !actionable.value || next === selected.value.mode) return;
  await action(async () => {
    await run({ method: 'set_mode', taskId: selected.value!.id, mode: next });
    await loadTasks();
    await loadEvents();
  });
}
async function changeClient() {
  await action(async () => {
    mode.value = modes.value.includes('ask_me') ? 'ask_me' : 'plan_only';
    await loadModels();
  });
}
async function loadHistory(next = false) {
  if (!project.value) return;
  await action(async () => {
    const key = project.value;
    const page = historySchema.parse(
      await run({
        method: 'history',
        projectKey: key,
        cursor: next ? history.value.nextCursor : null,
      }),
    );
    if (project.value !== key) return;
    history.value = {
      ...page,
      sessions: next ? [...history.value.sessions, ...page.sessions].slice(-100) : page.sessions,
    };
    historyLoaded.value = true;
  });
}
async function selectSession(entry: Session) {
  session.value = entry;
  summary.value = null;
  view.value = 'session';
  closeNarrowSidebar();
  await action(async () => {
    const result = summarySchema.parse(
      await run({ method: 'summary', projectKey: project.value, sessionId: entry.sessionId }),
    );
    if (session.value?.sessionId === entry.sessionId) summary.value = result;
  });
}
function recover() {
  void action(async () => {
    const request = uncertain.value;
    if (!request) return;
    if (request.operation.method === 'start') await completeStart(request);
    else await exchange(request);
    await loadTasks();
    await loadEvents();
  });
}
function openSidebar(which: 'tasks' | 'history') {
  sidebar.value = which;
  sidebarOpen.value = true;
}
watch(project, () => {
  history.value = { sessions: [], nextCursor: null, paged: true };
  historyLoaded.value = false;
  summary.value = null;
  session.value = null;
  if (view.value === 'session') view.value = selected.value ? 'task' : 'new';
});
watch(model, () => {
  effort.value = '';
});
function schedulePoll(delay = pollDelay) {
  clearTimeout(timer);
  if (mounted && !document.hidden) timer = setTimeout(() => void poll(), delay);
}
async function poll() {
  if (!mounted || document.hidden) return;
  connected.value = connection.value?.isOpen() ?? false;
  if (connected.value && !busy.value) {
    const before = JSON.stringify([tasks.value, cursor.value]);
    await action(async () => {
      await loadTasks();
      await loadEvents();
    }, true);
    const changed = before !== JSON.stringify([tasks.value, cursor.value]);
    pollDelay =
      changed || tasks.value.some((task) => task.state === 'running')
        ? 2000
        : Math.min(pollDelay * 2, 15000);
  }
  schedulePoll();
}
function visibilityChanged() {
  pollDelay = 2000;
  schedulePoll(0);
}
function leaveBrowserPage() {
  if (uncertain.value) {
    error.value = 'Resolve the pending action before leaving AI work.';
    return;
  }
  if (props.backHref) navigateBrowserTab(props.backHref);
}
function beforeUnload(event: BeforeUnloadEvent) {
  if (!uncertain.value) return;
  event.preventDefault();
  event.returnValue = '';
}
onMounted(() => {
  watch(
    uncertain,
    (pending) => {
      if (pending) window.addEventListener('beforeunload', beforeUnload);
      else window.removeEventListener('beforeunload', beforeUnload);
    },
    { immediate: true, flush: 'sync' },
  );
  document.addEventListener('visibilitychange', visibilityChanged);
  schedulePoll();
  if (props.device.nyx?.available && props.adapter?.openNyx) void connect();
});
onUnmounted(() => {
  mounted = false;
  clearTimeout(timer);
  window.removeEventListener('beforeunload', beforeUnload);
  document.removeEventListener('visibilitychange', visibilityChanged);
  void connection.value?.close();
});
</script>

<template>
  <main class="session nyx-page" :data-sidebar="sidebarOpen ? 'open' : 'closed'">
    <header class="session-bar">
      <a
        v-if="backHref"
        :href="backHref"
        class="session-brand"
        aria-label="Back to devices"
        :aria-disabled="!!uncertain"
        @click.prevent="leaveBrowserPage"
      >
        <BrandMark />
        <span class="session-brand-text"><b>ATUM Remote</b><span>AI work</span></span>
      </a>
      <button
        v-else
        type="button"
        class="session-brand nyx-page-back"
        aria-label="Back to device"
        @click="emit('back')"
      >
        <AppIcon name="back" />
        <span class="session-brand-text"><b>ATUM Remote</b><span>AI work</span></span>
      </button>
      <strong class="session-device">{{ device.name }}</strong>
      <StatusPill :tone="linkTone" :pulse="busy && !status">
        <span role="status">{{ linkLabel }}</span>
      </StatusPill>
      <span v-if="status" class="tag nyx-page-tag" :title="`Nyx Link ${status.version}`"
        >Nyx Link {{ status.version }}</span
      >
      <span
        v-if="status"
        class="tag nyx-page-tag"
        :title="
          canControl
            ? 'You can start, message and stop tasks'
            : 'You can watch tasks; decide in Nyx Link'
        "
      >
        <AppIcon :name="canControl ? 'key' : 'eye'" />{{
          canControl ? 'Task control' : 'Read only'
        }}
      </span>
      <span class="session-spacer" />
      <div class="session-controls nyx-page-controls">
        <button
          v-if="status"
          type="button"
          class="icon-button"
          :aria-expanded="sidebarOpen ? 'true' : 'false'"
          aria-controls="nyx-sidebar"
          :aria-label="sidebarOpen ? 'Hide task list' : 'Show task list'"
          :title="sidebarOpen ? 'Hide task list' : 'Show task list'"
          @click="sidebarOpen = !sidebarOpen"
        >
          <AppIcon name="panel" /><span v-if="attention && !sidebarOpen" class="nyx-badge">{{
            attention
          }}</span>
        </button>
        <button
          type="button"
          :disabled="busy || !device.nyx?.available"
          :title="connected ? 'Reconnect to Nyx Link' : 'Connect to Nyx Link'"
          @click="connect"
        >
          <AppIcon name="refresh" :class="{ spin: busy && !status }" />{{
            connected ? 'Reconnect' : 'Connect'
          }}
        </button>
        <button
          v-if="status && canControl"
          type="button"
          class="primary"
          :disabled="!connected"
          @click="newTask()"
        >
          <AppIcon name="plus" />New task
        </button>
      </div>
    </header>

    <div v-if="error" class="banner error nyx-page-banner" role="alert">
      <AppIcon name="warning" />
      <span>{{ error }}</span>
      <button
        type="button"
        class="icon-button quiet small"
        aria-label="Dismiss"
        @click="error = ''"
      >
        <AppIcon name="close" />
      </button>
    </div>
    <div v-if="uncertain" class="callout nyx-uncertain" data-tone="warn">
      <AppIcon name="warning" />
      <div>
        <strong>The last action may have been accepted</strong>
        <p>
          Check its result before issuing another action, or dismiss this once you have confirmed
          the task state yourself.
        </p>
        <div class="button-row">
          <button type="button" class="small" :disabled="busy || !connected" @click="recover">
            <AppIcon name="refresh" />Check request result
          </button>
          <button type="button" class="small quiet" :disabled="busy" @click="uncertain = null">
            I checked; dismiss
          </button>
        </div>
      </div>
    </div>

    <div class="nyx-body">
      <EmptyState v-if="!device.nyx?.available" icon="bolt" title="AI work is not available here">
        {{ device.nyx?.explanation ?? 'Nyx access is not available in this connection.' }}
        <template #hint>
          Nyx Link must run on {{ device.name }} with ATUM Remote enabled in its Settings. Browser
          access also needs the same Atum account and a Nyx device grant.
        </template>
      </EmptyState>

      <EmptyState
        v-else-if="!status"
        :icon="busy ? 'spinner' : 'link'"
        :tone="error ? 'danger' : 'neutral'"
        :title="busy ? 'Connecting to Nyx Link…' : 'Not connected to Nyx Link'"
      >
        <template v-if="busy">Opening the private link to Nyx Link on {{ device.name }}.</template>
        <template v-else-if="error">{{ error }}</template>
        <template v-else>
          Nyx Link must be running on {{ device.name }} with ATUM Remote enabled in its Settings.
        </template>
        <template #actions>
          <button type="button" class="primary" :disabled="busy" @click="connect">
            <AppIcon name="refresh" :class="{ spin: busy }" />{{ busy ? 'Connecting…' : 'Connect' }}
          </button>
        </template>
        <template #hint>
          Browser access requires the same Atum account and a Nyx device grant. Tasks keep running
          on the host whether or not this page is open.
        </template>
      </EmptyState>

      <div v-else class="nyx-shell">
        <aside id="nyx-sidebar" class="nyx-sidebar" aria-label="Tasks and history">
          <template v-if="sidebarOpen">
            <div class="nyx-sidebar-head">
              <div class="segmented nyx-sidebar-tabs" role="radiogroup" aria-label="Sidebar">
                <label>
                  <input v-model="sidebar" type="radio" name="nyx-sidebar" value="tasks" />
                  <AppIcon name="terminal" />Tasks<span v-if="attention" class="nyx-badge">{{
                    attention
                  }}</span>
                </label>
                <label>
                  <input v-model="sidebar" type="radio" name="nyx-sidebar" value="history" />
                  <AppIcon name="clock" />History
                </label>
              </div>
              <button
                type="button"
                class="icon-button quiet small nyx-sidebar-collapse"
                aria-label="Collapse sidebar"
                title="Collapse sidebar"
                @click="sidebarOpen = false"
              >
                <AppIcon name="back" />
              </button>
            </div>
            <NyxTaskList
              v-if="sidebar === 'tasks'"
              :tasks="tasks"
              :selected-id="view === 'task' ? (selected?.id ?? null) : null"
              :truncated="taskTruncated"
              :project-names="projectNames"
              :busy="busy"
              :can-create="canControl && connected"
              @select="selectTask"
              @create="newTask"
            />
            <NyxHistoryList
              v-else
              v-model:project="project"
              :projects="projects.projects"
              :sessions="history.sessions"
              :selected-id="view === 'session' ? (session?.sessionId ?? null) : null"
              :loaded="historyLoaded"
              :next-cursor="history.nextCursor"
              :paged="history.paged"
              :cloud-available="projects.cloudAvailable"
              :busy="busy"
              :connected="connected"
              @load="loadHistory()"
              @more="loadHistory(true)"
              @select="selectSession"
            />
          </template>
          <div v-else class="nyx-rail">
            <button
              type="button"
              class="icon-button quiet"
              aria-label="Expand sidebar"
              title="Expand sidebar"
              @click="sidebarOpen = true"
            >
              <AppIcon name="chevronRight" />
            </button>
            <button
              type="button"
              class="icon-button quiet nyx-rail-item"
              :aria-pressed="sidebar === 'tasks' ? 'true' : 'false'"
              aria-label="Tasks"
              title="Tasks"
              @click="openSidebar('tasks')"
            >
              <AppIcon name="terminal" /><span v-if="attention" class="nyx-badge">{{
                attention
              }}</span>
            </button>
            <button
              type="button"
              class="icon-button quiet nyx-rail-item"
              :aria-pressed="sidebar === 'history' ? 'true' : 'false'"
              aria-label="History"
              title="History"
              @click="openSidebar('history')"
            >
              <AppIcon name="clock" />
            </button>
            <button
              v-if="canControl"
              type="button"
              class="icon-button quiet nyx-rail-item"
              aria-label="New task"
              title="New task"
              :disabled="!connected"
              @click="newTask()"
            >
              <AppIcon name="plus" />
            </button>
          </div>
        </aside>

        <div class="nyx-main">
          <NyxNewTask
            v-if="view === 'new' && canControl"
            v-model:project="project"
            v-model:client="client"
            v-model:mode="mode"
            v-model:model="model"
            v-model:effort="effort"
            v-model:prompt="draft"
            :device-name="device.name"
            :projects="projects.projects"
            :projects-truncated="projects.truncated"
            :clients="status.clients"
            :modes="modes"
            :models="models"
            :efforts="efforts"
            :busy="busy"
            :can-start="canStart"
            @start="start"
            @client-change="changeClient"
          />
          <NyxSessionDetail
            v-else-if="view === 'session' && session"
            :session="session"
            :summary="summary"
            :busy="busy"
          />
          <div v-else-if="view === 'task' && selected" class="nyx-conversation">
            <div class="nyx-conversation-top">
              <header class="nyx-conversation-head">
                <div class="nyx-head-text">
                  <h2>{{ selectedProjectName }}</h2>
                  <div class="nyx-head-meta">
                    <StatusPill :tone="stateTone[selected.state]" :pulse="running">
                      {{ stateLabel[selected.state] }}
                    </StatusPill>
                    <span class="tag">{{ clientLabel[selected.client] }}</span>
                    <span
                      v-if="!selected.remote"
                      class="tag"
                      title="Started from Nyx Link on the host"
                      >On host</span
                    >
                  </div>
                </div>
                <div v-if="canControl" class="nyx-head-actions">
                  <button
                    type="button"
                    class="small quiet"
                    :disabled="!actionable || ended"
                    title="Interrupt the current turn; the task stays open"
                    @click="stop(true)"
                  >
                    Interrupt
                  </button>
                  <button
                    type="button"
                    class="small danger-quiet"
                    :disabled="!actionable || ended"
                    title="End the task and its process tree"
                    @click="stop(false)"
                  >
                    <AppIcon name="power" />End task
                  </button>
                </div>
              </header>
              <div
                v-if="selected.mode === 'auto_approve'"
                class="callout nyx-inline-callout"
                data-tone="warn"
              >
                <AppIcon name="bolt" />
                <div>
                  <strong>Auto-approve is on</strong>
                  <p>This task acts without asking you, within the client's own permissions.</p>
                </div>
              </div>
            </div>
            <NyxTranscript
              ref="transcript"
              :items="transcriptItems"
              :task-id="selected.id"
              :client="selected.client"
              :running="running"
              :truncated="truncated"
              :can-decide="canControl"
              :disabled="!actionable"
              @decide="decide"
              @answer="answer"
            />
            <div class="nyx-conversation-foot">
              <div v-if="pendingCount" class="nyx-attention" role="status">
                <AppIcon name="warning" />
                <span>{{
                  pendingCount === 1
                    ? 'The client is waiting for your decision.'
                    : `The client is waiting for ${pendingCount} decisions.`
                }}</span>
                <button type="button" class="small" @click="transcript?.scrollToPending()">
                  Show
                </button>
              </div>
              <NyxComposer
                v-if="canControl"
                v-model="prompt"
                :disabled="!actionable || ended"
                :can-send="actionable && selected.state === 'idle'"
                :running="running"
                :can-stop="actionable"
                :hint="composerHint(selected, canControl)"
                @send="send"
                @stop="stop(true)"
              >
                <template #chips>
                  <span
                    class="nyx-chip mono"
                    :title="selected.model ? 'Model' : 'Client default model'"
                  >
                    {{ selected.model ?? 'default model'
                    }}{{ selected.effort ? ` · ${selected.effort}` : '' }}
                  </span>
                  <label v-if="!ended && selectedModes.length > 1" class="nyx-chip nyx-chip-select">
                    <span class="sr-only">Mode</span>
                    <select :value="selected.mode" :disabled="!actionable" @change="setMode">
                      <option v-for="entry in selectedModes" :key="entry" :value="entry">
                        {{ modeLabel[entry] }}
                      </option>
                    </select>
                    <AppIcon name="chevronDown" />
                  </label>
                  <span v-else class="nyx-chip">{{ modeLabel[selected.mode] }}</span>
                </template>
              </NyxComposer>
              <p v-else class="nyx-readonly hint">
                <AppIcon name="eye" />{{ composerHint(selected, false) }}
              </p>
            </div>
          </div>
          <EmptyState v-else icon="terminal" title="Pick a task to follow it here">
            {{
              canControl
                ? 'Choose a hosted task from the list, or start a new one.'
                : 'Choose a hosted task from the list to watch its conversation.'
            }}
            <template v-if="canControl" #actions>
              <button type="button" class="primary" :disabled="!connected" @click="newTask()">
                <AppIcon name="plus" />New task
              </button>
            </template>
          </EmptyState>
        </div>
      </div>
    </div>
  </main>
</template>
